import { useCallback, useEffect, useState } from "react";
import { Users } from "lucide-react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Chip, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { ECBoard } from "@/components/ec/ECBoard";
import { ECHandover } from "@/components/ec/ECHandover";
import { ECMinutes } from "@/components/ec/ECMinutes";
import { api } from "@/lib/api";

const words = (s) => String(s || "").replace(/_/g, " ");

export default function ExecutiveCommittee() {
  const [overview, setOverview] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get("/ec/overview");
    setOverview(res.data);
  }, []);

  useEffect(() => { load().catch(() => setOverview({ error: true })); }, [load]);

  if (!overview) return <Loading label="Opening the Executive Committee" />;

  const current = overview.current || {};
  const previous = overview.previous || {};

  return (
    <div className="space-y-6" data-testid="ec-page">
      <PageHeader eyebrow="Governance · Executive Committee"
                  title="Executive Committee"
                  description="Everyone elected at the general election sits here — no roll to keep. Matters are discussed and voted in the open, and the outgoing and incoming committees settle their handover department by department." />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi label="On the committee" value={overview.counters.members} icon={Users}
             testId="ec-kpi-members"
             sub={current.term_start ? `Term ${current.term_start} to ${current.term_end}` : ""} />
        <Kpi label="Before the committee" value={overview.counters.open_matters}
             testId="ec-kpi-open" sub={`${overview.counters.in_voting} in voting`} />
        <Kpi label="Decided" value={overview.counters.decided} testId="ec-kpi-decided"
             sub={`Quorum on the ${words(overview.policy.ec_quorum)}`} />
        <Kpi label="Who may raise" value={words(overview.policy.ec_agenda_raised_by)}
             testId="ec-kpi-raise"
             sub={overview.policy.ec_visibility === "ec_only"
               ? "Committee only" : "Open to every voting member"} />
      </div>

      <Tabs defaultValue="board">
        <TabsList data-testid="ec-tabs">
          <TabsTrigger value="board" data-testid="tab-ec-board">White board</TabsTrigger>
          <TabsTrigger value="handover" data-testid="tab-ec-handover">Handover</TabsTrigger>
          <TabsTrigger value="minutes" data-testid="tab-ec-minutes">Minute sheets</TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-ec-members">The committee</TabsTrigger>
        </TabsList>

        <TabsContent value="board" className="mt-4"><ECBoard onChange={load} /></TabsContent>
        <TabsContent value="handover" className="mt-4"><ECHandover onChange={load} /></TabsContent>
        <TabsContent value="minutes" className="mt-4"><ECMinutes /></TabsContent>

        <TabsContent value="members" className="mt-4 space-y-4">
          <Panel title="The serving committee" testId="panel-ec-members"
                 kicker={current.election_name
                   ? `${current.election_name} · declared ${(current.declared_at || "").slice(0, 10)} · ${current.term_ref}`
                   : "No committee has been declared yet"}>
            <Table head={<><Th>Post</Th><Th>Member</Th><Th>Route</Th><Th>On the platform</Th></>}>
              {(current.members || []).map((m) => (
                <tr key={`${m.post_title}-${m.name}`} className="border-b border-border/60"
                    data-testid={`ec-member-${m.email || m.name}`}>
                  <Td>{m.post_title}</Td>
                  <Td className="font-medium text-foreground">{m.name}</Td>
                  <Td><Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                    {m.route}
                  </Chip></Td>
                  <Td className="font-mono text-[11px]">{m.email || "not on the platform"}</Td>
                </tr>
              ))}
            </Table>
          </Panel>

          <Panel title="The committee before them" testId="panel-ec-previous"
                 kicker={previous.term_ref
                   ? `${previous.election_name} · term ${previous.term_start} to ${previous.term_end}`
                   : "No earlier committee on the record"}>
            {(previous.members || []).length === 0 ? (
              <p className="text-[12.5px] text-muted-foreground">
                This is the first elected committee on the register.
              </p>
            ) : (
              <div className="space-y-1">
                {(previous.members || []).map((m) => (
                  <p key={`${m.post_title}-${m.name}`} className="text-[12px] text-muted-foreground">
                    <span className="text-foreground">{m.name}</span> — {m.post_title}
                  </p>
                ))}
              </div>
            )}
          </Panel>
        </TabsContent>
      </Tabs>
    </div>
  );
}
